import { Avatar, Box, Typography } from '@mui/material';
import React from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { COLOR as SHARED_COLOR } from 'shared/constants/colors';

import { EquinetPremium, EquinetTeams, RightArrowIcon } from '@/assets/svg';
import { Button, Modal } from '@/components';
import { useOrganisationsContext } from '@/context/OrganisationsContext';
import { useSubscriptionContext } from '@/context/SubscriptionContext';
import { useCanManageSubscription } from '@/hooks/useCanManageSubscription';
import { ROUTE } from '@/router/routes';
import { t } from '@/services/translations/config';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';

import { Props } from './types';

export const SubscriptionExpiredModal: React.FC<Props> = ({ onClose }) => {
    const navigate = useNavigate();
    const {
        currentOrganisationSubscriptionPlan,
        currentOrganisationSubscription,
    } = useSubscriptionContext();

    const { userOrganisation, userOrganisationsAvatarsUrls } =
        useOrganisationsContext();

    const isCurrentOrganisationOwner = useCanManageSubscription();

    const subscriptionType =
        currentOrganisationSubscriptionPlan &&
        currentOrganisationSubscriptionPlan?.seats > 1
            ? 'teams'
            : 'premium';
    const isTeamsSubscription = subscriptionType === 'teams';
    const hadSubscriptionBefore = currentOrganisationSubscription?.id;

    const teamAvatarUrl = userOrganisation
        ? userOrganisationsAvatarsUrls?.[userOrganisation.id]
        : undefined;

    if (!isCurrentOrganisationOwner || !hadSubscriptionBefore) {
        return null;
    }

    return (
        <Modal isOpen center styles="max-w-[447px] p-0 overflow-hidden">
            <Box
                className="flex justify-between items-center min-h-[110px] pl-[33px] pr-[27px] pb-[25px] pt-[32px]"
                style={{
                    background: isTeamsSubscription
                        ? `linear-gradient(180deg, ${COLOR.magnolia}, ${COLOR.prelude})`
                        : `linear-gradient(180deg, ${COLOR.linkWater}, ${COLOR.poloBlue})`,
                }}
            >
                <Box className="flex flex-col flex-1 gap-3 justify-center">
                    {isTeamsSubscription ? (
                        <EquinetTeams color={ENTITY_COLOR.teams} width={216} />
                    ) : (
                        <EquinetPremium
                            color={COLOR.deepCerulean}
                            width={216}
                        />
                    )}
                </Box>
            </Box>

            <Box className="p-[20px] pb-[25px] pl-[33px] pr-[27px] flex flex-col">
                <Box className="flex flex-col gap-[10px] mb-[20px]">
                    <Typography
                        fontSize={20}
                        fontWeight={700}
                        color={COLOR.ebonyClay}
                    >
                        {t('ExpiredPremiumModal:common:title', { spacing: '' })}
                    </Typography>
                    <Typography
                        fontSize={14}
                        fontWeight={700}
                        color={SHARED_COLOR.maroon}
                    >
                        {isTeamsSubscription
                            ? t(
                                  'ExpiredPremiumModal:for_teams:disabled_features_info',
                              )
                            : t(
                                  'ExpiredPremiumModal:personal:disabled_features_info',
                              )}
                    </Typography>
                </Box>
                <Box className="flex flex-col gap-[15px] mb-[18px]">
                    {isTeamsSubscription && (
                        <Box
                            className="flex gap-4 justify-between items-center px-3 py-2"
                            sx={{
                                border: `1px solid ${ENTITY_COLOR.teams}`,
                                backgroundColor: COLOR.white,
                                boxShadow: '0px 3px 6px #00000029',
                            }}
                        >
                            <Typography
                                color={COLOR.codGray}
                                fontSize={12}
                                lineHeight="20px"
                            >
                                {t(
                                    'ExpiredPremiumModal:for_teams:paused_team_info',
                                )}
                            </Typography>
                            <Avatar
                                src={teamAvatarUrl}
                                sx={{
                                    borderRadius: '10px',
                                    width: '50px',
                                    height: '50px',
                                }}
                                variant="square"
                            />
                        </Box>
                    )}

                    <Box
                        className="px-3 py-2"
                        sx={{
                            border: `1px solid ${SHARED_COLOR.maroon}`,
                            backgroundColor: COLOR.white,
                            boxShadow: '0px 3px 6px #00000029',
                        }}
                    >
                        <Typography
                            fontSize={12}
                            lineHeight="20px"
                            color={COLOR.codGray}
                            sx={{ whiteSpace: 'pre-line' }}
                        >
                            <Trans
                                i18nKey={
                                    isTeamsSubscription
                                        ? 'ExpiredPremiumModal:for_teams:inactive_subscription_info'
                                        : 'ExpiredPremiumModal:personal:inactive_subscription_info'
                                }
                                components={{
                                    italic: <i />,
                                }}
                            />
                        </Typography>
                    </Box>
                </Box>
                <Typography fontSize={12} fontWeight={700}>
                    {isTeamsSubscription
                        ? t('ExpiredPremiumModal:for_teams:footer')
                        : t('ExpiredPremiumModal:personal:footer')}
                </Typography>

                <Box className="flex gap-[22px] self-end mt-[33px]">
                    <Button
                        sx={{ color: COLOR.tundora, fontSize: 12 }}
                        caption={t('ExpiredPremiumModal:common:dismiss')}
                        onClick={onClose}
                    />
                    <Button
                        sx={{
                            backgroundColor: isTeamsSubscription
                                ? ENTITY_COLOR.teams
                                : COLOR.deepCerulean,
                            color: COLOR.white,
                            '&:hover': {
                                backgroundColor: isTeamsSubscription
                                    ? ENTITY_COLOR.teams
                                    : COLOR.deepCerulean,
                                opacity: 0.8,
                            },
                            fontSize: 12,
                        }}
                        variant="contained"
                        caption={t(
                            'ExpiredPremiumModal:common:renew_subscription',
                        )}
                        endIcon={<RightArrowIcon color={COLOR.white} />}
                        onClick={() => navigate(ROUTE.premium)}
                    />
                </Box>
            </Box>
        </Modal>
    );
};
